import { apiService } from '../../services/api';
import type {
  AccessCandidate,
  AccessSummary,
  Confidentiality,
  GrantEntityType,
} from './types';

const BASE = '/api/security/records';

/** One short line a user can act on, never a raw stack or a bare status code. */
export function confidentialityErrorMessage(error: any): string {
  const data = error?.response?.data;
  const message = data?.error?.message ?? data?.message;
  if (Array.isArray(message)) return message.join(', ');
  if (typeof message === 'string' && message.trim()) return message;
  if (error?.response?.status === 404) return 'That record is no longer available.';
  if (error?.response?.status === 403) return 'You may not change access on this record.';
  return 'Something went wrong. Try again.';
}

export const confidentialityApi = {
  /** Who can see this record, and why. 404 when the caller cannot see it. */
  async access(
    entityType: GrantEntityType,
    entityId: string,
  ): Promise<AccessSummary> {
    const { data } = await apiService.get(`${BASE}/${entityType}/${entityId}/access`);
    return data;
  },

  /** People who could be added, for the picker. */
  async candidates(
    entityType: GrantEntityType,
    entityId: string,
  ): Promise<AccessCandidate[]> {
    const { data } = await apiService.get(
      `${BASE}/${entityType}/${entityId}/candidates`,
    );
    return data;
  },

  async grant(
    entityType: GrantEntityType,
    entityId: string,
    userId: string,
    reason?: string,
  ): Promise<AccessSummary> {
    const { data } = await apiService.post(
      `${BASE}/${entityType}/${entityId}/grants`,
      { userId, reason },
    );
    return data;
  },

  async revoke(
    entityType: GrantEntityType,
    entityId: string,
    userId: string,
    reason?: string,
  ): Promise<AccessSummary> {
    const { data } = await apiService.delete(
      `${BASE}/${entityType}/${entityId}/grants/${userId}`,
      { data: { reason } },
    );
    return data;
  },

  async setLevel(
    entityType: GrantEntityType,
    entityId: string,
    confidentiality: Confidentiality,
    note?: string,
  ): Promise<AccessSummary> {
    const { data } = await apiService.patch(
      `${BASE}/${entityType}/${entityId}/confidentiality`,
      { confidentiality, note },
    );
    return data;
  },
};
